import React, { useState } from 'react'
import styled from 'styled-components'
import { useTheme } from '../context/ThemeContext'
import { useAuth } from '../context/AuthContext'
import { useNavigate } from 'react-router-dom'
import { Search, Users, Clock, Trophy, Play } from 'lucide-react'

const Container = styled.div`
  padding: 40px;
  background-color: ${props => props.theme.backgroundColor};
  min-height: calc(100vh - 80px);
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;
`

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
  margin: 0 0 10px 0;
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`

const Subtitle = styled.p`
  font-size: 18px;
  color: ${props => props.theme.textSecondary};
  margin: 0 0 30px 0;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`

const SearchSection = styled.div`
  margin-bottom: 40px;
`

const SearchBar = styled.div`
  position: relative;
  max-width: 600px;
  margin: 0 auto;
`

const SearchInput = styled.input`
  width: 100%;
  padding: 16px 50px 16px 20px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  font-size: 16px;
  background-color: #f8f9fa;
  color: ${props => props.theme.textPrimary};
  
  &::placeholder {
    color: #999;
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimary};
  }
`

const SearchButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  color: #999;
  
  &:hover {
    color: ${props => props.theme.buttonPrimary};
  }
`

const GameModeSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-bottom: 50px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`

const GameModeCard = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 20px;
  padding: 30px;
  text-align: center;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`

const GameModeIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  background: ${props => props.$gradient};
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 32px;
`

const GameModeTitle = styled.h3`
  font-size: 24px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
  margin: 0 0 10px 0;
`

const GameModeDescription = styled.p`
  color: ${props => props.theme.textSecondary};
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 20px 0;
`

const PlayButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 25px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 auto;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
  }
`

const RecentGamesSection = styled.div`
  margin-bottom: 40px;
`

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
  margin: 0 0 20px 0;
`

const GamesList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
`

const GameCard = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }
`

const GameInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
`

const GameTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
  margin: 0;
  flex: 1;
`

const GameStats = styled.div`
  display: flex;
  gap: 15px;
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
`

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const RoomCodeSection = styled.div`
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  border-radius: 20px;
  padding: 40px;
  text-align: center;
  color: white;
  margin-bottom: 40px;
`

const RoomCodeTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 10px 0;
`

const RoomCodeDescription = styled.p`
  font-size: 16px;
  margin: 0 0 30px 0;
  opacity: 0.9;
`

const RoomCodeInputContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`

const RoomCodeInput = styled.input`
  width: 300px;
  max-width: 100%;
  padding: 15px 20px;
  border: none;
  border-radius: 10px;
  font-size: 18px;
  text-align: center;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`

const JoinButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 2px solid white;
  padding: 13px 30px;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;
  height: 52px;
  
  &:hover {
    background: white;
    color: #f5576c;
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`

export default function PlayPage() {
  const { theme } = useTheme()
  const { isLoggedIn } = useAuth()
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [roomCode, setRoomCode] = useState('')

  // 샘플 게임 데이터
  const recentGames = [
    {
      id: 1,
      title: '과학: 오로라란 무엇일까요?',
      players: 24,
      duration: '5분',
      score: 850
    },
    {
      id: 2,
      title: '역사: 한글을 만든 세종대왕',
      players: 18,
      duration: '7분',
      score: 720
    },
    {
      id: 3,
      title: '수학: 기본 연산 마스터',
      players: 32,
      duration: '4분',
      score: 950
    }
  ]

  const handleJoinRoom = () => {
    if (roomCode.trim()) {
      // 방 참가 로직
      console.log('방 참가:', roomCode)
    }
  }

  const handleGameModeSelect = (mode) => {
    if (!isLoggedIn && mode !== 'guest') {
      // 로그인 필요 알림
      alert('로그인이 필요한 기능입니다.')
      navigate('/login')
      return
    }
    
    console.log('게임 모드 선택:', mode)
    // 게임 모드별 로직 구현
  }

  return (
    <Container theme={theme}>
      <Header>
        <Title theme={theme}>게임하기</Title>
        <Subtitle theme={theme}>친구들과 함께 즐거운 학습 게임을 시작해보세요!</Subtitle>
      </Header>

      <RoomCodeSection>
        <RoomCodeTitle>방 번호로 참가하기</RoomCodeTitle>
        <RoomCodeDescription>
          선생님이 공유한 방 코드를 입력하여 게임에 참가하세요
        </RoomCodeDescription>
        <RoomCodeInputContainer>
          <RoomCodeInput
            type="text"
            placeholder="방 번호 입력"
            value={roomCode}
            onChange={(e) => setRoomCode(e.target.value)}
          />
          <JoinButton onClick={handleJoinRoom}>
            참가하기
          </JoinButton>
        </RoomCodeInputContainer>
      </RoomCodeSection>

      <SearchSection>
        <SearchBar>
          <SearchInput
            theme={theme}
            type="text"
            placeholder="게임 검색하기"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <SearchButton theme={theme}>
            <Search size={20} />
          </SearchButton>
        </SearchBar>
      </SearchSection>

      <GameModeSection>
        <GameModeCard theme={theme} onClick={() => handleGameModeSelect('solo')}>
          <GameModeIcon $gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
            🎯
          </GameModeIcon>
          <GameModeTitle theme={theme}>솔로 플레이</GameModeTitle>
          <GameModeDescription theme={theme}>
            혼자서 문제를 풀며 실력을 향상시켜보세요
          </GameModeDescription>
          <PlayButton>
            <Play size={16} />
            시작하기
          </PlayButton>
        </GameModeCard>

        <GameModeCard theme={theme} onClick={() => handleGameModeSelect('multiplayer')}>
          <GameModeIcon $gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)">
            👥
          </GameModeIcon>
          <GameModeTitle theme={theme}>멀티플레이어</GameModeTitle>
          <GameModeDescription theme={theme}>
            친구들과 함께 실시간으로 경쟁해보세요
          </GameModeDescription>
          <PlayButton>
            <Users size={16} />
            방 만들기
          </PlayButton>
        </GameModeCard>

        <GameModeCard theme={theme} onClick={() => handleGameModeSelect('challenge')}>
          <GameModeIcon $gradient="linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)">
            🏆
          </GameModeIcon>
          <GameModeTitle theme={theme}>챌린지 모드</GameModeTitle>
          <GameModeDescription theme={theme}>
            시간 제한 내에서 최고 점수에 도전하세요
          </GameModeDescription>
          <PlayButton>
            <Trophy size={16} />
            도전하기
          </PlayButton>
        </GameModeCard>
      </GameModeSection>

      <RecentGamesSection>
        <SectionTitle theme={theme}>최근 플레이한 게임</SectionTitle>
        <GamesList>
          {recentGames.map(game => (
            <GameCard key={game.id} theme={theme}>
              <GameInfo>
                <GameTitle theme={theme}>{game.title}</GameTitle>
              </GameInfo>
              <GameStats theme={theme}>
                <StatItem>
                  <Users size={12} />
                  {game.players}명
                </StatItem>
                <StatItem>
                  <Clock size={12} />
                  {game.duration}
                </StatItem>
                <StatItem>
                  <Trophy size={12} />
                  {game.score}점
                </StatItem>
              </GameStats>
            </GameCard>
          ))}
        </GamesList>
      </RecentGamesSection>
    </Container>
  )
}